import React, { useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikConfig } from 'formik';

import DatePickerTag4 from '@/components/common/DatePicker/DatePickerTag4';
import { getTomorrowsDate } from '@/utils/date.utils';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import useUploadFile, { UploadUpliftFileFields } from './hooks/useUploadFile';
import { validationSchema } from './validationSchema';
import FileInput from '@/components/common/FileInput/FileInput';
import { useGetUploadedUpliftFiles } from './hooks/useGetUploadedUpliftFiles';
import { UPLIFT_UPLOADED_FILES_PER_PAGE } from '@/config/common.config';
import { useAddQueryToUrl } from '@/components/common/TagTabs/useAddQueryToUrl';
import SmallText from '@/components/common/SmallText/SmallText';
import { notificationService } from '@/services/notification/notification.client';
import styles from './UploadTemplate.module.scss';

export default function UploadFileCard() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { onFileUpload } = useUploadFile();
  const { getUploadedFiles } = useGetUploadedUpliftFiles();
  const { getValue } = useAddQueryToUrl();
  const { getUserRole } = useGetUserRole();
  const pageIndex = parseInt(getValue('page') ?? '1', 10);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingValues, setPendingValues] = useState<UploadUpliftFileFields | null>(null);
  const [pendingResetForm, setPendingResetForm] = useState<(() => void) | null>(null);
  const [userRole, setUserRole] = useState<UserRole>();

  useEffect(() => {
    getUserRole().then(setUserRole);
  }, [getUserRole]);

  const isFullAccess = userRole === UserRole.FULL_ACCESS;

  const resetFileInput = (resetForm: () => void) => {
    resetForm();
    getUploadedFiles(pageIndex, UPLIFT_UPLOADED_FILES_PER_PAGE);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit: FormikConfig<UploadUpliftFileFields>['onSubmit'] = (values, { resetForm }) => {
    const callback = () => resetFileInput(resetForm);

    const onDuplicateFileName = () => {
      if (isFullAccess) {
        setPendingValues(values);
        setPendingResetForm(() => resetForm);
        setShowDuplicateModal(true);
      } else {
        notificationService.open({
          title: 'Duplicate file detected',
          text: 'A file with this name and effective date has already been uploaded.'
            + ' Upload was not allowed.',
          type: 'danger'
        });
      }
    };

    onFileUpload({ values, callback, options: { onDuplicateFileName } });
  };

  const onConfirmAllowDuplicates = () => {
    setShowDuplicateModal(false);
    if (pendingValues && pendingResetForm) {
      const callback = () => resetFileInput(pendingResetForm);
      onFileUpload({ values: pendingValues, callback, options: { allowDuplicates: true } });
    }
    setPendingValues(null);
    setPendingResetForm(null);
  };

  return (<>
    <Formik<UploadUpliftFileFields>
      initialValues={{
        effectiveDate: undefined,
        fileUpload: undefined
      }}
      onSubmit={onSubmit}
      validateOnMount={true}
      validationSchema={validationSchema}
    >
      {({ errors, setFieldValue, setFieldTouched, touched, values }) => (
        <Form>
          <div className="row ag-uplift-search">
            <div className="col ag-uplift-search__col">
              <div className="row">
                <div className={styles.uploadRow}>

                  <label
                    className="mb-10 pb-10 fw-500"
                    htmlFor="effectiveDate"
                  >
                    Uplift Effective Date {<span className={styles.required}>*</span>}
                  </label>
                  <div className="col-xxl-3 col-xl-3 col-lg-4 col-md-6 mb-sm-20 w-30">
                    <DatePickerTag4
                      id="effectiveDate"
                      value={values.effectiveDate}
                      placeholder={ getTomorrowsDate()}
                      onChange={({ value }) => {
                        setFieldTouched('effectiveDate', true, false);
                        setFieldValue('effectiveDate', value);
                      }}
                    />
                    {errors.effectiveDate && touched.effectiveDate && (
                      <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                        {errors.effectiveDate}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className={styles.uploadRow}>
                  <span className={styles.uploadText}>
                    Upload File
                  </span>
                  <div className="col-xxl-3 col-xl-3 col-lg-4 col-md-6 mb-sm-20 w-30">
                    <FileInput
                      id="fileUpload"
                      disabled={!values.effectiveDate || !!errors.effectiveDate}
                      onChange={(event) => setFieldValue('fileUpload', event.target?.files?.[0])}
                      format={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                      ref={fileInputRef}
                    />
                    <SmallText className={styles.fileUploadWarning}>
                      Only files of xlsx format can be uploaded
                    </SmallText>
                  </div>
                  <div className={styles.fileSubmitBtn}>
                    <Button
                      type="submit"
                      buttonStyleType={ButtonStyleType.PRIMARY}
                      disabled={!values.effectiveDate || !values.fileUpload || !!errors.effectiveDate}
                    >
                      Upload
                    </Button>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>

    {showDuplicateModal && isFullAccess && (
      <AgConfirmationModal
        title="Duplicate file detected"
        isOpen={showDuplicateModal}
        onClose={() => setShowDuplicateModal(false)}
        onConfirm={onConfirmAllowDuplicates}
        closeText="Cancel"
        confirmText="Upload anyway"
        closeOnConfirm={false}
      >
        <p>
          A file with this name and effective date has already been uploaded.
          Would you like to upload it anyway?
        </p>
      </AgConfirmationModal>
    )}
  </>);
}
